import { PropsWithoutRef, useMemo } from "react";
import {
  Drawer,
  Toolbar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  IconButton,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import SummarizeTwoToneIcon from "@mui/icons-material/SummarizeTwoTone";
import CloseIcon from "@mui/icons-material/Close";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import EmailIcon from "@mui/icons-material/Email";
import PsychologyIcon from "@mui/icons-material/Psychology";
import QuizIcon from "@mui/icons-material/Quiz";
import SavingsIcon from "@mui/icons-material/Savings";
import { SIDEBAR_WIDTH } from "../constants";
import useAuth from "../hooks/useAuth";
import {
  NAVIGATION_ITEMS,
  getFirstViewablePermissionRoute,
  parsePermissions,
} from "../constants/navigation";

interface Props {
  mobileOpen: boolean;
  handleSidebarToggle: () => void;
}

export default function Sidebar({
  mobileOpen,
  handleSidebarToggle,
}: PropsWithoutRef<Props>) {
  const userData = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const items = useMemo(
    () =>
      NAVIGATION_ITEMS.map((item) => ({
        ...item,
        icon:
          {
            Dashboard: <DashboardIcon />,
            "Check Feedback": <PeopleIcon />,
            "Manage Customers": <PeopleIcon />,
            "Manage Subscription Plans": <SubscriptionsIcon />,
            "Manage Categories": <ListAltIcon />,
            "Set Points Criteria": <ManageAccountsIcon />,
            "Manage Savings Limit": <SavingsIcon />,
            "Manage Round Up": <PsychologyIcon />,
            "Manage Marketplace": <ViewCarouselIcon />,
            "Manage Email Templates": <EmailIcon />,
            "Manage Notifications": <NotificationsActiveIcon />,
            "Reports and Analytics": <BarChartIcon />,
            "Manage CMS": <SummarizeTwoToneIcon />,
            "Manage FAQs": <QuizIcon />,
            "Manage Sub-Admin": <PersonAddIcon />,
            Settings: <SettingsIcon />,
          }[item.label] || <DashboardIcon />,
      })),
    []
  );

  const userPermissions = useMemo(
    () => parsePermissions(userData?.permissions),
    [userData?.permissions]
  );

  const list = useMemo(() => {
    if (userData?.role === "ADMIN") {
      return items;
    }

    if (!userPermissions.length) {
      return [];
    }

    return items.filter((item) => {
      const matchedPermission = userPermissions.find(
        (permission) => permission?.label === item.label
      );

      return Boolean(matchedPermission?.isView);
    });
  }, [items, userData?.role, userPermissions]);

  const handleNavigation = () => {
    if (userData?.role === "SUB") {
      const subAdminPath = getFirstViewablePermissionRoute(userData?.permissions);
      navigate(subAdminPath || "/dashboard");
      return;
    }

    navigate("/dashboard");
  };

  const goToPath = (path: string) => {
    navigate(path);

    if (mobileOpen) {
      handleSidebarToggle();
    }
  };

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { lg: 0 } }}
      className="sidebar-main-div"
    >
      <Drawer
        className="sidebar_drawer"
        variant="temporary"
        open={mobileOpen}
        onClose={handleSidebarToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: SIDEBAR_WIDTH,
          },
        }}
      >
        <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
          <figure>
            <img src="/static/images/logo.png" alt="SaverStride" onClick={handleNavigation} />
          </figure>
          <IconButton
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
              color: "white",
              display: { lg: "none" },
            }}
            onClick={handleSidebarToggle}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
        {list.map((item) => {
          const isSelected =
            location.pathname === item.path ||
            location.pathname.startsWith(`${item.path}/`);

          return (
            <List key={item.key} className="sidebr-lst" sx={{ padding: 0 }}>
              <ListItemButton
                className={`lst-itm ${isSelected ? "Mui-selected" : ""}`}
                onClick={() => goToPath(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText className="lstitm-txt" primary={item.label} />
              </ListItemButton>
            </List>
          );
        })}
      </Drawer>
      <Drawer
        variant="permanent"
        className="sidebar_drawer"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
        open
      >
        <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
          <figure>
            <img src="/static/images/logo.png" alt="SaverStride" onClick={handleNavigation} />
          </figure>
        </Toolbar>
        {list.map((item) => {
          const isSelected =
            location.pathname === item.path ||
            location.pathname.startsWith(`${item.path}/`);

          return (
            <List key={`desktop-${item.key}`} className="sidebr-lst" sx={{ padding: 0 }}>
              <ListItemButton
                className={`lst-itm ${isSelected ? "Mui-selected" : ""}`}
                onClick={() => goToPath(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText className="lstitm-txt" primary={item.label} />
              </ListItemButton>
            </List>
          );
        })}
      </Drawer>
    </Box>
  );
}
